import { computed, ref } from 'vue';
import { loadCountries, type Country } from '@/api/countries';
import { fiatCurrencies } from '@/api/currencies';
import { useCountryLabel } from '@/utils/countryName';
import { mapApiError } from '@/utils/apiError';

/**
 * Reference data and error text the concierge screens share: the country list (names from the ISO code in the
 * client's language through Intl.DisplayNames, as every vitrine screen does — the server's English name is the
 * fallback), the fiat currency suggestions, and the vitrine error dialect
 * (`{error: CODE}` → `vitrine.error.<CODE>`).
 */
export function useConciergeReference(t: (key: string) => string) {
  const countries = ref<Country[]>([]);
  const fiat = ref<string[]>([]);
  const countryLabel = useCountryLabel(countries);

  async function loadReference(): Promise<void> {
    const [c, f] = await Promise.all([
      loadCountries().catch(() => [] as Country[]),
      fiatCurrencies().then(list => list.map(x => x.code)).catch(() => [] as string[]),
    ]);
    countries.value = c;
    fiat.value = f;
  }

  const countryOptions = computed(() => countries.value
    .map(c => ({ value: c.alpha3, label: countryLabel(c.alpha3) }))
    .sort((a, b) => a.label.localeCompare(b.label)));

  function errorText(e: unknown): string {
    return mapApiError(e, t, { prefix: 'vitrine.error.', field: 'error' });
  }

  return { countries, fiat, countryLabel, countryOptions, loadReference, errorText };
}
